import type { QueryClient } from '@tanstack/react-query';
import {
  isQuestionListKey,
  isResolveQuestionKey,
  questionDetailKey,
  questionInteractionsKey,
  questionByUuidKey,
} from '@lib/cache/questions';
import { helperFeedBaseKey } from '@lib/cache/helperFeed';

/**
 * Cancels every in-flight query whose queryFn writes to the question entity
 * store (via upsertQuestionEntities) for `questionId`. Call this from the
 * onMutate of any mutation that optimistically writes the entity store —
 * otherwise a producer's refetch can land after the optimistic write and
 * clobber it with stale server data (upsertQuestionEntities overwrites
 * unconditionally).
 *
 * Adding a new entity-store producer elsewhere? Add its key/predicate here —
 * every existing caller of this helper gets the coverage for free instead of
 * drifting out of sync with a hand-maintained cancel list per hook.
 */
export const cancelEntityProducers = (
  queryClient: QueryClient,
  questionId: string,
): Promise<unknown> =>
  Promise.all([
    queryClient.cancelQueries({ predicate: query => isQuestionListKey(query.queryKey) }),
    queryClient.cancelQueries({ predicate: query => isResolveQuestionKey(query.queryKey) }),
    queryClient.cancelQueries({ queryKey: helperFeedBaseKey() }),
    queryClient.cancelQueries({ queryKey: questionDetailKey(questionId), exact: true }),
    queryClient.cancelQueries({ queryKey: questionInteractionsKey(questionId), exact: true }),
    queryClient.cancelQueries({ queryKey: questionByUuidKey(questionId), exact: true }),
  ]);
